import { useState } from 'react';
import type { MouseEvent } from 'react';
import type { PulseConnectionResponse } from '@orion/contracts';
import { Avatar, Button, DataTable, EmptyState, Pill } from '@orion/ui';
import { useAuth } from '../../../lib/auth.js';
import { ApiError } from '../../../lib/api-client.js';
import { useToast } from '../../../lib/toast.js';
import { PROVIDER_LABEL } from './PulseConnectionCard.js';
import { PulseConnectionDrawer } from './PulseConnectionDrawer.js';
import { useAllPulseConnectionsQuery, usePulseConnectionCountsQuery, useRemindPulseOwner } from './api.js';

const STATUS_PILL_COLOR: Record<PulseConnectionResponse['status'], string> = {
  active: '#1E7A4C',
  needs_reconnect: '#8A4B06',
  disabled: '#5E6470',
};

const PROVIDER_OPTIONS: Array<{ value: PulseConnectionResponse['provider'] | ''; label: string }> = [
  { value: '', label: 'All providers' },
  { value: 'google', label: 'Google Workspace' },
  { value: 'microsoft', label: 'Microsoft 365' },
  { value: 'imap', label: 'IMAP + App Password' },
];

const PAGE_SIZE = 12;

function NeedsAttentionRow({ connection, isMine, onOpen }: { connection: PulseConnectionResponse; isMine: boolean; onOpen: () => void }) {
  const remind = useRemindPulseOwner();
  const toast = useToast();
  const ownerFirstName = connection.ownerDisplayName.split(' ')[0];

  const sendReminder = async (e: MouseEvent) => {
    e.stopPropagation();
    try {
      await remind.mutateAsync(connection.id);
      toast.success(`Reminder sent to ${connection.ownerDisplayName}.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Failed to send reminder.');
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onOpen();
      }}
      className="flex w-full cursor-pointer items-center gap-3.5 rounded-card border border-amber bg-surface p-3.5 text-left hover:bg-surface-subtle"
    >
      <Avatar name={connection.ownerDisplayName} size="md" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13.5px] font-semibold text-ink">
          {connection.emailAddress} <span className="font-normal text-text-faint">· {connection.ownerDisplayName} · {PROVIDER_LABEL[connection.provider]}</span>
        </div>
        <div className="truncate text-xs text-danger">{connection.lastSyncError ?? 'Access may have expired or been revoked.'}</div>
      </div>
      {isMine ? (
        <span className="shrink-0 text-xs font-semibold text-primary">Reconnect it &rarr;</span>
      ) : (
        <Button size="sm" variant="secondary" loading={remind.isPending} onClick={sendReminder}>
          Remind {ownerFirstName}
        </Button>
      )}
    </div>
  );
}

/** Admin > Integrations > Inbox Pulse's "All connections" tab
 * (`admin.integrations.manage`) — every mailbox connection in the tenant,
 * not just the signed-in user's own (`listMine`, the "My mailbox" tab).
 * Mixes the two approaches shown in the design review: connections needing
 * reconnection stay fully visible up top regardless of filters (so a
 * handful of breakages never get buried among dozens of healthy ones), and
 * everything else lives in a searchable, filterable, paginated table rather
 * than 50 stacked cards. */
export function PulseAllConnectionsView() {
  const { session } = useAuth();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<PulseConnectionResponse['status'] | undefined>(undefined);
  const [provider, setProvider] = useState<PulseConnectionResponse['provider'] | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);

  const { data: counts } = usePulseConnectionCountsQuery();
  const attention = useAllPulseConnectionsQuery({ status: 'needs_reconnect', page: 1, pageSize: 100 });
  const table = useAllPulseConnectionsQuery({ q: search.trim() || undefined, status, provider, page, pageSize: PAGE_SIZE });

  const rows = table.data?.data ?? [];
  const total = table.data?.meta?.total ?? 0;
  const attentionRows = attention.data?.data ?? [];

  const isMine = (c: PulseConnectionResponse) => c.ownerMembershipId === session?.membershipId;
  const openConnection = rows.find((r) => r.id === openId) ?? attentionRows.find((r) => r.id === openId) ?? null;

  const toggleStatus = (next: PulseConnectionResponse['status'] | undefined) => {
    setStatus((cur) => (cur === next ? undefined : next));
    setPage(1);
  };

  const chipClass = (active: boolean, activeClass: string) =>
    `flex h-7 items-center gap-1.5 rounded-full px-3 text-xs font-semibold transition-colors ${active ? activeClass : 'bg-neutral-pill-soft text-neutral-pill hover:bg-surface-subtle'}`;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => toggleStatus(undefined)} className={chipClass(status === undefined, 'bg-ink text-white')}>
          {counts?.total ?? '—'} total
        </button>
        <button type="button" onClick={() => toggleStatus('active')} className={chipClass(status === 'active', 'bg-success-soft text-success')}>
          {counts?.active ?? '—'} active
        </button>
        <button type="button" onClick={() => toggleStatus('needs_reconnect')} className={chipClass(status === 'needs_reconnect', 'bg-amber text-ink')}>
          {counts?.needsReconnect ?? '—'} need reconnecting
        </button>
        <button type="button" onClick={() => toggleStatus('disabled')} className={chipClass(status === 'disabled', 'bg-surface-sunken text-text-secondary')}>
          {counts?.disabled ?? '—'} disabled
        </button>
      </div>

      {attentionRows.length > 0 && (
        <div className="flex flex-col gap-2">
          <h2 className="text-[13px] font-semibold text-ink">Needs attention</h2>
          <div className="flex flex-col gap-2">
            {attentionRows.map((c) => (
              <NeedsAttentionRow key={c.id} connection={c} isMine={isMine(c)} onOpen={() => setOpenId(c.id)} />
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          type="search"
          placeholder="Search by email or owner…"
          className="h-9 max-w-xs flex-1 rounded-control border border-border-strong bg-surface px-3 text-sm"
        />
        <select
          value={provider ?? ''}
          onChange={(e) => {
            const v = e.target.value as PulseConnectionResponse['provider'] | '';
            setProvider(v === '' ? undefined : v);
            setPage(1);
          }}
          className="h-9 rounded-control border border-border-strong bg-surface px-2.5 text-sm"
        >
          {PROVIDER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {!table.isLoading && rows.length === 0 && <EmptyState title="No connections match" description="Try a different search or filter." />}

      {rows.length > 0 && (
        <DataTable
          rows={rows}
          rowKey={(c) => c.id}
          onRowClick={(c) => setOpenId(c.id)}
          rowClassName={(c) => (c.status === 'needs_reconnect' ? 'bg-amber-soft/50' : undefined)}
          columns={[
            {
              key: 'mailbox',
              header: 'Mailbox',
              width: '2.2fr',
              render: (c) => (
                <div className="flex items-center gap-2.5">
                  <Avatar name={c.ownerDisplayName} size="sm" />
                  <div className="min-w-0">
                    <div className="truncate font-medium text-ink">{c.emailAddress}</div>
                    <div className="truncate text-xs text-text-faint">{c.ownerDisplayName}</div>
                  </div>
                </div>
              ),
            },
            { key: 'provider', header: 'Provider', width: '1.3fr', render: (c) => PROVIDER_LABEL[c.provider] },
            { key: 'status', header: 'Status', width: '1.2fr', render: (c) => <Pill label={c.status.replace('_', ' ')} color={STATUS_PILL_COLOR[c.status]} /> },
            {
              key: 'lastSync',
              header: 'Last sync',
              width: '1.3fr',
              render: (c) => (c.lastSyncAt ? new Date(c.lastSyncAt).toLocaleString('en-US') : 'Never yet'),
            },
            {
              key: 'activity',
              header: 'Activity',
              width: '1.3fr',
              render: (c) => (c.stats ? `${c.stats.processed} processed · ${c.stats.leadsCreated} leads` : '—'),
            },
          ]}
        />
      )}

      {total > 0 && (
        <div className="flex items-center justify-between text-sm text-text-muted">
          <span>
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex gap-2">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-control border border-border-strong px-2.5 py-1 disabled:opacity-40">
              Previous
            </button>
            <button
              type="button"
              disabled={page * PAGE_SIZE >= total}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-control border border-border-strong px-2.5 py-1 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {openConnection && <PulseConnectionDrawer connection={openConnection} isMine={isMine(openConnection)} onClose={() => setOpenId(null)} />}
    </div>
  );
}
